import { Box } from '../UI-CHAKRA/Box';
import { Flex } from '../UI-CHAKRA/Flex';
import { Text } from '../UI-CHAKRA/Text';

import { LogoImg } from '../../containers/certificate/CertificateCard';
import { Button } from '../UI-CHAKRA/Button';

import { EditIcon } from '@chakra-ui/icons';
import { useBreakpointValue } from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ReactComponent as AlertIcon } from '../../assets/images/icons/mevo/alert.svg';
import { ReactComponent as CloseIcon } from '../../assets/images/icons/mevo/close-mono-color.svg';
import { CERTIFIER_STATUS } from '../../containers/certificate/certificates';
import { DialogWrappedComponentProps, FeedbackContext } from '../../providers/FeedbackProvider';
import { PrescriptionContext } from '../../providers/PrescriptionProvider';
import { usePrescriptionStore } from '../../stores/prescriptionStore';
import CertificateList from './CertificateList';
import { certificates } from '../../containers/certificate/certificates';
import { amplitudeModule } from '../../shared/amplitude';

interface DialogCertificatesProps {
  onSelectCertificate(value: string): void;
}
export default function DialogCertificates({ onSelectCertificate }: DialogCertificatesProps) {
  const dialogWidth = useBreakpointValue({ base: '95%', md: '575px' });
  const { loadingPrescriberCoreData } = useContext(PrescriptionContext);

  const { selectedCertificateType } = usePrescriptionStore();
  const prescriberConfiguration = usePrescriptionStore((store) => store.prescriberConfiguration);
  const prescription = usePrescriptionStore((store) => store.prescription);

  const userCertificates = usePrescriptionStore((store) => store.userCertificates());

  const selectedUserCertificate = useMemo(() => {
    return (
      userCertificates.find((certificate) => certificate.value === selectedCertificateType) ??
      certificates.find((certificate) => certificate.value === selectedCertificateType)
    );
  }, [selectedCertificateType, userCertificates]);

  const { openBaseDialog } = useContext(FeedbackContext);

  const [HoverComponent, setHoverComponent] = useState<React.ReactNode | null>(null);

  const openStandardSignatureDialog = useCallback(() => {
    amplitudeModule.esignatureManage({
      partner_id: prescriberConfiguration?.idParceiro ?? 0,
      partner_name: prescriberConfiguration?.Nome ?? '',
      establishment_name: prescription?.Estabelecimento?.Nome ?? '',
    });

    return openBaseDialog(
      (dialogProps: DialogWrappedComponentProps) => (
        <CertificateList
          onSelect={(val) => {
            onSelectCertificate(val);
            dialogProps.onClose();
          }}
          onClose={() => dialogProps.onClose()}
          selectedCertificateType={selectedCertificateType}
        />
      ),
      {
        width: dialogWidth,
        height: 'auto',
        persist: false,
      }
    );
  }, [
    dialogWidth,
    onSelectCertificate,
    openBaseDialog,
    selectedCertificateType,
    prescriberConfiguration,
    prescription?.Estabelecimento?.Nome,
  ]);

  useEffect(() => {
    window.addEventListener('go-back-to-select-certificate', openStandardSignatureDialog);

    return () => {
      window.removeEventListener('go-back-to-select-certificate', openStandardSignatureDialog);
    };
  }, [openStandardSignatureDialog]);

  const shouldShowCertifierStatus =
    selectedUserCertificate?.certifierStatus === CERTIFIER_STATUS.INACTIVE ||
    selectedUserCertificate?.certifierStatus === CERTIFIER_STATUS.PARTIALLY_ACTIVE;

  const showCertifierStatusOnMouseEnter = () => {
    if (!shouldShowCertifierStatus) return null;

    return setHoverComponent(
      <Flex
        pos={'absolute'}
        top="10px"
        left="10px"
        bottom="50%"
        right="10px"
        bgColor={
          selectedUserCertificate?.certifierStatus === CERTIFIER_STATUS.INACTIVE
            ? 'rgba(255, 97, 97, 0.9)'
            : 'rgba(252, 178, 36, 0.9)'
        }
        flexDir={'column'}
        justifyContent={'center'}
        alignItems={'center'}
        p={'10px'}
        borderRadius={'18px'}
      >
        <Box mb={'5px'}>
          {selectedUserCertificate?.certifierStatus === CERTIFIER_STATUS.INACTIVE ? (
            <CloseIcon fill="white" width={'20px'} height={'20px'} />
          ) : (
            <AlertIcon width={'20px'} height={'20px'} />
          )}
        </Box>
        <Text
          fontSize={'12px'}
          fontWeight={600}
          lineHeight={'13px'}
          textAlign={'center'}
          color={selectedUserCertificate?.certifierStatus === CERTIFIER_STATUS.INACTIVE ? 'white' : 'typography.dark'}
        >
          Certificadora
          {selectedUserCertificate?.certifierStatus === CERTIFIER_STATUS.INACTIVE ? ' indisponível' : ' instável'}
        </Text>
      </Flex>
    );
  };

  const showSelectedCertifierStatusIcon = useMemo(() => {
    if (!shouldShowCertifierStatus) return null;

    if (selectedUserCertificate?.certifierStatus === CERTIFIER_STATUS.PARTIALLY_ACTIVE) {
      return <AlertIcon width={'13px'} height={'13px'} data-testid={'certifier-instable'} />;
    }

    if (selectedUserCertificate?.certifierStatus === CERTIFIER_STATUS.INACTIVE) {
      return <CloseIcon fill="#FF6161" width={'13px'} height={'13px'} data-testid={'certifier-inactive'} />;
    }
  }, [selectedUserCertificate?.certifierStatus, shouldShowCertifierStatus]);

  return (
    <>
      <Box bgColor="white" borderRadius="20px" p="10px">
        {selectedUserCertificate ? (
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              key={selectedUserCertificate.value}
              transition={{
                type: 'spring',
              }}
            >
              <Flex
                width="100%"
                p={'15px 10px'}
                gap="20px"
                justifyContent="center"
                alignItems="center"
                onMouseEnter={showCertifierStatusOnMouseEnter}
                onMouseLeave={() => setHoverComponent(null)}
              >
                {showSelectedCertifierStatusIcon}{' '}
                {selectedUserCertificate.imagePath ? (
                  <LogoImg src={selectedUserCertificate.imagePath} alt={selectedUserCertificate.name} />
                ) : (
                  <Text
                    textAlign={'center'}
                    fontSize={'12px'}
                    dangerouslySetInnerHTML={{ __html: selectedUserCertificate.name }}
                  />
                )}
                {HoverComponent}
                <Flex flex={1} flexDir="column" alignItems="flex-start">
                  <Text align={'center'} fontSize={'12px'} lineHeight={'16px'} fontWeight={700}>
                    Método de assinatura:
                  </Text>
                  <Text align={'center'} fontSize={'12px'} lineHeight={'16px'} data-testid="selected-certificate">
                    {selectedUserCertificate.name.replace('</br>', '')}
                  </Text>
                </Flex>
              </Flex>
            </motion.div>
          </AnimatePresence>
        ) : (
          <Flex
            flexDirection="column"
            width="100%"
            p="10px 20px"
            mb="10px"
            gap="10px"
            justifyContent="center"
            alignItems="flex-start"
          >
            <Text fontSize="14px" fontWeight={700} lineHeight="none">
              Nenhum certificado digital selecionado
            </Text>
            <Text fontSize="12px" lineHeight="none">
              Clique em “Gerenciar assinatura digital” para selecionar.
            </Text>
          </Flex>
        )}
        {userCertificates.length >= 1 && (
          <Flex p="5px" mt={'10px'}>
            <Button
              bgColor="#F1F3F5"
              color="black"
              fontSize="14px"
              w="100%"
              borderRadius="20px"
              justifyContent="space-evenly"
              onClick={openStandardSignatureDialog}
              rightIcon={<EditIcon width="14px" fill="black" />}
              data-testid="emission-manage-certificate"
              data-cy="emission-manage-certificate"
              isLoading={loadingPrescriberCoreData}
            >
              Gerenciar assinatura digital
            </Button>
          </Flex>
        )}
      </Box>
    </>
  );
}
